import { ServerError } from 'common/types/errors';
import ky, { isHTTPError } from 'ky';

export class NetworkError extends Error {
    constructor(
        public readonly url: string,
        cause: unknown,
    ) {
        super(`Could not fetch ${url}`, { cause });
    }
}

const api = ky.extend({
    timeout: 60000,
    // Send the better-auth session cookie on every request.
    credentials: 'include',
    async fetch(input, init) {
        try {
            return await fetch(input, init);
        } catch (error) {
            const url =
                typeof input === 'string'
                    ? input
                    : input instanceof URL
                      ? input.toString()
                      : input.url;
            throw new NetworkError(url, error);
        }
    },
    hooks: {
        beforeError: [
            async ({ error, request }) => {
                if (!isHTTPError(error)) {
                    return error;
                }

                const text = await error.response.text().catch(() => '');

                return ServerError.fromResponse(
                    request.method,
                    request.url,
                    error.response.status,
                    text,
                    error,
                );
            },
        ],
    },
});

export async function apiUpload(
    url: string,
    body: Blob,
    onProgress?: (loaded: number, total: number) => void,
    retries = 0,
) {
    return new Promise<string>((resolve, reject) => {
        try {
            const req = new XMLHttpRequest();

            function onError(networkError: boolean) {
                if (retries) {
                    resolve(apiUpload(url, body, onProgress, retries - 1));
                } else {
                    if (networkError) {
                        reject(new NetworkError(url, 'Upload failed'));
                    } else {
                        reject(ServerError.fromResponse('POST', url, req.status, req.responseText));
                    }
                }
            }

            if (onProgress) req.upload.onprogress = (e) => onProgress(e.loaded, e.total);
            req.onload = () => {
                if (req.status !== 200) {
                    onError(false);
                } else {
                    resolve(JSON.parse(req.responseText));
                }
            };
            req.onerror = () => onError(true);
            req.onabort = () => onError(true);

            req.open('POST', url);
            req.withCredentials = true;

            req.send(body);
        } catch (e) {
            reject(e);
        }
    });
}

export default api;
Object.assign(globalThis, { api });
